import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type Dispatch, type ReactNode, type SetStateAction } from "react";
import { Link } from "@tanstack/react-router";
import { yetkiliMi, type MontajKaydiYaniti, type MontajOlcumHatti, type MontajOlcumTipi, type MontajOnerisi, type MontajRiserYaniti, type MontajSatirYaniti } from "@oasis/domain";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";
import { siraliYenile } from "@/lib/mrb-toplu";
import { useOturum } from "@/lib/oturum";
import { dosyaYukle, tarih } from "@/lib/veri";
import { Band, Buton, Cip, Etiket, Hata, Kutu, Kv, Sayfa, alan, etiket, girdi, tabloBas, tabloHucre } from "@/components/parcalar";
import { belgeLinki, sayfaAlanlari, sayfaEtiketi, usePdfSec } from "@/components/pdf-sayfa";
import { MONTAJ_UCU, akisRaporIstegi, asmRaporKaldir, dosyaRiserSorusu, kaldirmaSorusu, kaldirmayiYurut, listeSatiriMi, montajKaldir, olcuGruplari, satirDurumEtiketi, type OlcuHucresi } from "@/lib/montaj";
import {
  ALAN_ADI,
  FORM_ALAN_SINIRI,
  FORM_DURUMLARI,
  FORM_OLCU_SAYISI,
  ONAY_SATIRLARI,
  ROL_SATIRLARI,
  adetOku,
  baslikOneriMevcut,
  bekleyenYaziVar,
  birimEksigi,
  birimSayisi,
  birimlerAcik,
  birimlerAyni,
  birimlerYamasi,
  durumDegisimiSorar,
  durumYamasi,
  etkinAdet,
  formAdimi,
  formEtiketleri,
  formYuzdesi,
  gorunenAlanlar,
  isSirasi,
  kayittakiBirimler,
  montajFormGuncelle,
  montajFormImzala,
  montajFormYazdir,
  ndtBandi,
  olcuGirdiMetni,
  olcuYamasi,
  onayGovdesi,
  oneriDurumu,
  oneriEslemesi,
  oneriGruplari,
  oneriMevcut,
  oneriUygula,
  rolUyarisi,
  satirGovdesi,
  topluOneriKaynaklari,
  topluOneriSayisi,
  topluOneriUygula,
  topluOneriYamasi,
  yazdirmaIpucu,
  yuvaAlanYaz,
  yuvaDegistir,
  yuvalariKur,
  yuvalariTamamla,
  type FormSatirDurumu,
  type MontajFormGovdesi,
  type OneriGrubu,
  type SatirYamasi,
  type Yuva,
} from "@/lib/montaj-form";
import { MontajSatirBlogu } from "./Montaj";
import { MontajSureklilik } from "./MontajSureklilik";

/**
 * D-068: FM-0012'nin ekranda doldurulan formu (kayıt türü `form`; riserın Assembly sayfası kayıt form türündeyse bunu çizer).
 * Form C basımını izler: 30 satır, sekiz ölçü, onay bloğu. Üç hâl: açık · print-out üretildi, imzalı çıktı bekleniyor (alanlar
 * hâlâ değişebilir) · imzalı çıktıyla kapandı (hiçbir alan değişmez).
 *
 * "Save" düğmesi yoktur: her alan odaktan çıkınca (düğme ve seçicide tıklanınca) yalnız o alan gönderilir; sunucu sayfanın tam
 * durumunu döner. İstekler sırayla gider (`isSirasi`): bir alanın gövdesi önceki alanın yanıtından sonra kurulur. Kaydı tutmayan
 * alanın yazılan değeri ekranda kalır, alan kırmızı çerçeve alır ve sunucunun cümlesi sayfanın başında ve onay kutusunun üstünde
 * durur (o alan yeniden kaydedilene kadar).
 *
 * Başka kayıttan gelen hiçbir kimlik kendiliğinden yazılmaz: riser kaydı, OEM kitabı, söküm kaydı ve stok seçimi öneri çipidir;
 * değer kişi "Use" ile aldığında ya da kendisi yazdığında kayda girer. Kimlik yan etkisi yoktur: riserın Assembly SN'i, komponent
 * SN'leri ve MRB bağı buradan yazılmaz. Sistem çıktı üretmez: "Generate print-out" yalnız işaret yazar, imzalar kâğıtta atılır,
 * imzalı belge Finishing'in Assembly raporu olarak (var olan uç ve NDT kapısıyla) geri yüklenir. Saf mantık `lib/montaj-form.ts`'tedir.
 */

/** Formun basılı başlık sabitleri (C basımı); veritabanına yazılmaz. Etiketler formun basılı terimleridir, çevrilmez. */
const FM12_BASLIK: readonly (readonly [string, string])[] = [
  ["Oil States Ref. No.", "453228 (452909/A)"],
  ["Customer Name", "Turkish Petroleum"],
  ["Customer Ref No.", "Agreement No. 1606251405"],
  ["Item Description", "ORG Risers (75ft)"],
  // Kâğıt formda "21-1/14" basılıdır (basım hatası); ekran söküm formundaki ve montaj resmindeki "21-1/4" değerini yazar.
  ["Assembly Model", '75ft, Ø21-1/4" OD x 0.875" WT'],
  ["Max. Working Pressure", "500 PSI"],
  ["Assembly Part No.", "OEM: H157203-2H593 · OSI: A56026001190"],
];

const hataMetni = (e: unknown): string => (e instanceof Error && e.message ? e.message : cevir("Operation failed"));

/**
 * Kaydı tutmayan alanın kırmızı çerçevesi. Sınıf önemli (`!`) yazılır: taban stildeki katman dışı `* { border-color }` kuralı
 * katmanlı renk sınıflarını ezer, ünlemsiz yazılan sınıf ekranda çizilmez (emsal: `components/pdf-sayfa.tsx`).
 */
const HATA_CERCEVESI = "border-bad!";

/** Süren düğme işlemi: düğmesi döner, öteki düğmeler kilitlenir. */
type Mesgul = "yazdir" | "imzali" | "kaldir";

/**
 * Odaktan çıkınca kaydedilen alanın durumu. Yazılırken yerel taslak görünür; kayıt tutunca sunucudaki değer. Kayıt tutmazsa
 * yazılan değer alanda kalır (kişi düzeltip yeniden çıkar) ve alan kırmızı çerçeve alır. Sunucudaki değer başka yoldan değişirse
 * (öneri alındı, durumla birlikte Fitted By yazıldı) ve alan odakta değilse taslak bırakılır. `yaz` kırpılmış metni alır;
 * değerin değişip değişmediğine gönderim anında o karar verir. Alan kapanınca (kayıt kapandı) kaydedilmemiş taslak bırakılır:
 * kapalı kayıtta yalnız kayıttaki değer görünür.
 */
function useAlan<E extends HTMLInputElement | HTMLTextAreaElement>(deger: string, kapali: boolean, yaz: (metin: string) => Promise<boolean>) {
  const ref = useRef<E>(null);
  const [taslak, setTaslak] = useState<string | null>(null);
  const [hatali, setHatali] = useState(false);
  useEffect(() => {
    if (!kapali && document.activeElement === ref.current) return;
    setTaslak(null);
    setHatali(false);
  }, [deger, kapali]);
  const cik = async () => {
    // Sayı ve tarih girdisinde tarayıcının okuyamadığı yazım boş metin gelir: boş sayılıp kayıttaki değer silinmesin.
    if (ref.current instanceof HTMLInputElement && ref.current.validity.badInput) {
      setHatali(true);
      return;
    }
    if (taslak === null) return;
    const gonderilen = taslak;
    const ok = await yaz(gonderilen.trim());
    setHatali(!ok);
    if (ok) setTaslak((s) => (s === gonderilen ? null : s));
  };
  return { ref, deger: taslak ?? deger, taslak, hatali, degis: setTaslak, cik: () => void cik() };
}

/** Tek satırlık girdi; odaktan çıkınca kaydedilir (`useAlan`). */
function Girdi({ deger, yaz, kapali, className, type = "text", placeholder, maxLength, yaninda }: {
  /** kayıttaki değer, alanda göründüğü biçimde */
  deger: string;
  /** kırpılmış metni kaydeder; false: yazılmadı */
  yaz: (metin: string) => Promise<boolean>;
  kapali: boolean;
  className?: string;
  type?: "text" | "number" | "date";
  placeholder?: string;
  maxLength?: number;
  /** girdinin hemen ardından çizilen (öneri çipleri); alanda duran kaydedilmemiş metni alır, yoksa null */
  yaninda?: (taslak: string | null) => ReactNode;
}) {
  const a = useAlan<HTMLInputElement>(deger, kapali, yaz);
  return (
    <>
      {/* Kapalı alanda örnek metin (placeholder) gösterilmez: kayıt verisi gibi okunmasın. */}
      <input ref={a.ref} type={type} className={cn(girdi, className, a.hatali && HATA_CERCEVESI)} value={a.deger} placeholder={kapali ? undefined : placeholder} maxLength={maxLength} disabled={kapali} autoComplete="off" spellCheck={false} onChange={(e) => a.degis(e.target.value)} onBlur={a.cik} />
      {yaninda?.(a.taslak)}
    </>
  );
}

/** Çok satırlı alan; odaktan çıkınca kaydedilir (`useAlan`). */
function MetinAlani({ deger, yaz, kapali, satir, maxLength }: { deger: string; yaz: (metin: string) => Promise<boolean>; kapali: boolean; satir: number; maxLength: number }) {
  const a = useAlan<HTMLTextAreaElement>(deger, kapali, yaz);
  return <textarea ref={a.ref} rows={satir} className={cn(alan, a.hatali && HATA_CERCEVESI)} value={a.deger} maxLength={maxLength} disabled={kapali} onChange={(e) => a.degis(e.target.value)} onBlur={a.cik} />;
}

/**
 * Bir alanın (ya da bir birimin alanlarının) öneri çipleri: kaynağı adıyla. Alan boşsa değer ve "Use"; alan aynı değeri
 * taşıyorsa gri "same as …" (aynı kaynak cümlesi bir kez yazılır); başka değer taşıyorsa değer ve "differs" (düğme yok).
 * Önerilen değer kişi "Use"a basmadan alana yazılmaz. Birimin önerileri birden çok alana gidiyorsa çipte alanın adı da yazar.
 */
function OneriCipleri({ gruplar, mevcut, kullan }: { gruplar: readonly OneriGrubu[]; mevcut: (g: OneriGrubu) => string | null | undefined; kullan: (g: OneriGrubu) => void }) {
  const alanli = new Set(gruplar.map((g) => g.alan)).size > 1;
  const yazilan = new Set<string>();
  return (
    <>
      {gruplar.map((g, n) => {
        const kaynak = g.kaynaklar.join(" · ");
        const durum = oneriDurumu(g, mevcut(g));
        if (durum === "ayni") {
          const metin = cevir("same as {0}", kaynak);
          if (yazilan.has(metin)) return null;
          yazilan.add(metin);
          return <span key={n} className="text-xs text-ink-3">{metin}</span>;
        }
        return (
          <span key={n} className="inline-flex min-w-0 flex-wrap items-center gap-1 text-xs text-ink-2">
            {kaynak}{alanli && g.alan !== "assemblySn" ? ` · ${ALAN_ADI[g.alan]}` : ""}: <b className="min-w-0 break-words font-mono">{g.deger}</b>
            {durum === "bos" ? <Buton kucuk islem="assembly" onClick={() => kullan(g)}>{cevir("Use")}</Buton> : <Etiket tone="warn">{cevir("differs")}</Etiket>}
          </span>
        );
      })}
    </>
  );
}

/** Satırların düzenleme kipine verilenler. */
interface Duzenleme {
  /** kayıt kapalı: öneri çipleri, "+ more fields" ve "Use all" çizilmez */
  kapali: boolean;
  /** girdiler kapalı: kayıt kapalı ya da kişinin formu doldurma yetkisi yok */
  salt: boolean;
  oneriler: readonly MontajOnerisi[];
  /** "Fitted By" varsayılanı: satırın alanı boşken durum seçilince satıra yazılır */
  fittedBy: string;
  /** Satırın alan güncellemesi. Gövde gönderim anındaki satırdan kurulur; null dönerse istek gitmez. true: kayıt tuttu (ya da gerekmedi). */
  satirYaz: (sira: number, alanAdi: string, kur: (guncel: MontajSatirYaniti) => SatirYamasi | null) => Promise<boolean>;
}

/**
 * FM-0012'nin bir satırı, düzenleme kipinde: durum düğmeleri · adet (basılıdan farklıysa) · birim yuvaları (rol, kimlik alanları,
 * öneri çipleri) · Fitted By ve not. Liste satırlarında (cıvata 9, pul 10, locking insert 11, buoyancy 30) birim yuvaları ve adet
 * her durumda açıktır ve durum değişince kimlikler silinmez; öteki satırlarda yalnız "fitted"de açıktır, başka duruma geçerken
 * onayla silinir. Bir yuvadaki alan değişince satırın birimlerinin TAMAMI gider (sunucu diziyi bütün olarak yerine koyar).
 * Birim alanları yerel taslaktan çizilir: kişi satırda yazarken gelen yanıt yazdığını bozmaz; kayıt tutunca kayıttaki birimler
 * görünür (kimliği boş yuva kayıtta tutulmaz, arkasındakiler bir öne kayar). Öneri çipleri bu yüzden yuvaya konumla değil
 * DEĞERLE düşer (`oneriEslemesi`): satırda duran değerin önerisi o değerin durduğu yuvada "same as …" der, alınmamış öneriler boş
 * kalan yuvalarda sunulur. Birimlerin kaydı tutmazsa satırın birim blokları kırmızı çerçeve alır (yazılan değerler ekranda kalır).
 */
function SatirDuzenle({ x, d }: { x: MontajSatirYaniti; d: Duzenleme }) {
  const [taslak, setTaslak] = useState<readonly Yuva[] | null>(null);
  /** "+ more fields": satırın bütün birimlerinde yedi alan ve birim notu açılır. */
  const [hepsi, setHepsi] = useState(false);
  /** Satırın birimlerinin son kaydı tutmadı (sunucu reddetti ya da istek düştü); birimler yeniden kaydedilince düşer. */
  const [birimHatasi, setBirimHatasi] = useState(false);
  // Kayıt kapanınca kaydedilmemiş taslak bırakılır: kapalı kayıtta yalnız kayıttaki birimler görünür.
  useEffect(() => {
    if (!d.kapali) return;
    setTaslak(null);
    setBirimHatasi(false);
  }, [d.kapali]);
  const liste = listeSatiriMi(x.sira);
  // Kayıttaki birim hiçbir durumda gizlenmez.
  const acik = birimlerAcik(x.sira, x.durum) || x.birimler.length > 0;
  const sayi = Math.max(birimSayisi(x.sira, etkinAdet(x)), x.birimler.length);
  const kayittaki = yuvalariKur(x.birimler, sayi);
  const yuvalar = taslak ? yuvalariTamamla(taslak, sayi) : kayittaki;
  const eksik = birimEksigi(x);
  const rolSecilir = ROL_SATIRLARI.includes(x.sira);
  // Çiplerin ve "Use all"un okuduğu yuvalar: SN alanında çipin karşılaştırdığı değerle (kişi alanı değiştirmediyse kaydın temiz değeri).
  const numarali = yuvalar.map((y, i) => ({ seq: i + 1, ...y, sn: oneriMevcut("sn", y, x.birimler[i]) }));
  // Hangi öneri takımı hangi yuvada çizilir: konum değil değer ("Use all" da aynı eşlemeyi okur).
  const esleme = d.kapali || !acik ? [] : oneriEslemesi(d.oneriler, x.sira, numarali);
  const toplu = d.kapali || !acik ? [] : topluOneriKaynaklari(x.sira, d.oneriler).map((t) => ({ ...t, n: topluOneriSayisi(topluOneriYamasi({ sira: x.sira, birimler: numarali }, d.oneriler, t.kaynak)) })).filter((t) => t.n > 0);

  /** Satırın birimlerini gönderir. Kayıttakiyle aynıysa istek gitmez; kimliği boş yuvadaki rol ve not taslakta bekler. */
  const gonder = async (yeni: readonly Yuva[]) => {
    const yama = birimlerYamasi(yeni);
    const durum = { gitti: false };
    const ok = await d.satirYaz(x.sira, "birimler", (g) => {
      if (birimlerAyni(yama, kayittakiBirimler(g.birimler))) return null;
      durum.gitti = true;
      return { birimler: yama };
    });
    setBirimHatasi(!ok);
    if (ok && (durum.gitti || !bekleyenYaziVar(yeni))) setTaslak((s) => (s === yeni ? null : s));
  };
  const yaz = (yeni: readonly Yuva[]) => {
    setTaslak(yeni);
    void gonder(yeni);
  };
  const kullan = (i: number, g: OneriGrubu) => {
    const yeni = oneriUygula(yuvalar, i, g);
    if (yeni !== yuvalar) yaz(yeni);
  };
  const hepsiniKullan = (kaynak: MontajOnerisi["kaynak"]) => {
    const yama = topluOneriYamasi({ sira: x.sira, birimler: numarali }, d.oneriler, kaynak);
    if (yama.length) yaz(topluOneriUygula(yuvalar, yama));
  };
  /** Seçili düğmeye yeniden basmak durumu boşaltır. */
  const durumSec = async (secilen: FormSatirDurumu) => {
    const yeni = x.durum === secilen ? null : secilen;
    if (durumDegisimiSorar({ sira: x.sira, durum: x.durum, birimler: yuvalar }, yeni) && !window.confirm(cevir("Change the status of row {0}? The identities entered for it are deleted.", x.sira))) return;
    const siler = !liste && x.durum === "takildi" && yeni !== "takildi";
    const ok = await d.satirYaz(x.sira, "durum", (g) => durumYamasi(g, yeni, d.fittedBy));
    if (ok && siler) {
      setTaslak(null);
      setBirimHatasi(false);
    }
  };
  const adetYaz = async (metin: string) => {
    const a = adetOku(metin);
    if (a.hata) return false;
    return d.satirYaz(x.sira, "qty", (g) => (g.qty === a.deger ? null : { qty: a.deger }));
  };
  const cik = () => {
    if (taslak) void gonder(taslak);
  };

  return (
    <MontajSatirBlogu
      x={x}
      duzenle={{
        rozet: eksik && <Etiket tone="warn">{cevir("{0} of {1} units entered", eksik.girilen, eksik.beklenen)}</Etiket>,
        govde: (
          <>
            <div className="ml-6 mt-2 flex flex-wrap gap-1.5">
              {FORM_DURUMLARI.map((s) => (
                <Buton key={s} islem="assembly" tur={x.durum === s ? "birincil" : "normal"} disabled={d.salt} onClick={() => durumSec(s)}>{satirDurumEtiketi(s).metin}</Buton>
              ))}
            </div>
            {acik && (
              <div className="ml-6 mt-2 flex flex-wrap items-center gap-2 text-sm">
                <span className="text-xs text-ink-3">{cevir("Qty (if not as printed)")}</span>
                <span className="inline-block w-20"><Girdi type="number" deger={x.qty == null ? "" : String(x.qty)} kapali={d.salt} yaz={adetYaz} /></span>
                <span className="text-xs text-ink-3">{x.basiliQty == null ? cevir("number of modules fitted") : cevir("printed: {0}", x.basiliQty)}</span>
                {toplu.map((t) => (
                  <Fragment key={t.kaynak}>
                    {toplu.length > 1 && <span className="text-xs text-ink-3">{t.ad}</span>}
                    <Buton kucuk islem="assembly" title={t.ad} onClick={() => hepsiniKullan(t.kaynak)}>{cevir("Use all ({0})", t.n)}</Buton>
                  </Fragment>
                ))}
              </div>
            )}
            {acik && yuvalar.map((y, i) => (
              <div key={i} className={cn("ml-6 mt-2 rounded-md border border-line p-2", birimHatasi && HATA_CERCEVESI)}>
                <div className="flex flex-wrap items-center gap-2">
                  <Etiket>{i + 1}</Etiket>
                  {rolSecilir && (
                    <span className="inline-block w-28">
                      <select className={girdi} value={y.rol ?? ""} disabled={d.salt} onChange={(e) => yaz(yuvaDegistir(yuvalar, i, { rol: e.target.value === "C" || e.target.value === "K" ? e.target.value : null }))}>
                        <option value="">{cevir("select...")}</option>
                        <option value="C">Choke</option>
                        <option value="K">Kill</option>
                      </select>
                    </span>
                  )}
                  {rolUyarisi(x.sira, y) && <Etiket tone="warn">{cevir("C&K role not selected")}</Etiket>}
                  {!d.kapali && <OneriCipleri gruplar={oneriGruplari(d.oneriler, x.sira, esleme[i] ?? null)} mevcut={(g) => (g.alan === "assemblySn" ? null : numarali[i]?.[g.alan])} kullan={(g) => kullan(i, g)} />}
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {gorunenAlanlar(x.sira, y, hepsi, kayittaki[i]).map((a) => (
                    <label key={a} className="block min-w-0">
                      <span className={etiket}>{ALAN_ADI[a]}</span>
                      <input className={girdi} value={y[a]} maxLength={FORM_ALAN_SINIRI[a]} disabled={d.salt} autoComplete="off" spellCheck={false} onChange={(e) => setTaslak(yuvaAlanYaz(yuvalar, i, a, e.target.value))} onBlur={cik} />
                    </label>
                  ))}
                  {(hepsi || Boolean(y.not.trim()) || Boolean(kayittaki[i]?.not.trim())) && (
                    <label className="col-span-2 block min-w-0">
                      <span className={etiket}>{cevir("Unit note")}</span>
                      <input className={girdi} value={y.not} maxLength={FORM_ALAN_SINIRI.birimNotu} disabled={d.salt} autoComplete="off" onChange={(e) => setTaslak(yuvaAlanYaz(yuvalar, i, "not", e.target.value))} onBlur={cik} />
                    </label>
                  )}
                </div>
                {!d.kapali && !hepsi && <div className="mt-2"><Buton kucuk onClick={() => setHepsi(true)}>{cevir("+ more fields")}</Buton></div>}
              </div>
            ))}
            {/* "Note" etiketi girdisiyle birlikte durur: dar ekranda ikisi birden alt satıra kırılır. */}
            <div className="ml-6 mt-2 flex flex-wrap items-center gap-2 text-sm">
              <label className="contents">
                <span className="text-xs text-ink-3">Fitted By</span>
                <Girdi className="max-w-[200px]" maxLength={FORM_ALAN_SINIRI.fittedBy} deger={x.fittedBy ?? ""} kapali={d.salt} yaz={(m) => d.satirYaz(x.sira, "fittedBy", (g) => ((g.fittedBy ?? "") === m ? null : { fittedBy: m || null }))} />
              </label>
              <label className="flex min-w-[12rem] flex-1 items-center gap-2">
                <span className="shrink-0 text-xs text-ink-3">{cevir("Note")}</span>
                <Girdi className="max-w-sm" maxLength={FORM_ALAN_SINIRI.satirNotu} placeholder={cevir("e.g. x2 fit on rig, x1 fitted")} deger={x.not ?? ""} kapali={d.salt} yaz={(m) => d.satirYaz(x.sira, "not", (g) => ((g.not ?? "") === m ? null : { not: m || null }))} />
              </label>
            </div>
          </>
        ),
      }}
    />
  );
}

/**
 * Bir ölçü girdisi (inç): ondalık ya da kesirli yazılır. Kesirli girilen ölçü yazıldığı gibi görünür, altında ondalık karşılığı
 * durur. Okunamayan yazım gönderilmez (altında kırmızı cümle); aralık dışı değeri sunucu reddeder. Söküm öncesi ölçü yalnız
 * gösterilir: fark hesabı ve tolerans uyarısı yoktur.
 */
function OlcuGirdisi({ ad, hucre, kapali, yaz }: { ad: string; hucre: OlcuHucresi; kapali: boolean; yaz: (metin: string) => Promise<"yazildi" | "bicim" | "ret"> }) {
  const [bicim, setBicim] = useState(false);
  return (
    <div className="min-w-0">
      <label className="block">
        <span className={etiket}>{ad}</span>
        <Girdi
          deger={olcuGirdiMetni(hucre)}
          kapali={kapali}
          maxLength={FORM_ALAN_SINIRI.olcuYazimi}
          yaz={async (m) => {
            const s = await yaz(m);
            setBicim(s === "bicim");
            return s === "yazildi";
          }}
        />
      </label>
      {hucre.yazim && hucre.deger != null && <div className="mt-0.5 text-xs text-ink-3 tnum">{hucre.deger}</div>}
      {bicim && <div className="mt-0.5 text-xs text-bad">{cevir("Enter inches as a decimal or a fraction, for example 3.625 or 3-5/8.")}</div>}
      {hucre.onceki != null && <div className="mt-0.5 text-xs text-ink-3">{cevir("before disassembly: {0}", hucre.onceki)}</div>}
    </div>
  );
}

/** Doluluk satırı: çubuk ve sayım (durumu seçili satırlar, girilmiş ölçüler). */
function Doluluk({ k }: { k: MontajKaydiYaniti }) {
  const y = formYuzdesi(k);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="inline-flex h-2 w-44 overflow-hidden rounded-full bg-surface-3"><i className="block h-full bg-ok" style={{ width: `${y.yuzde}%` }} /></span>
      <span className="text-sm text-ink-2">{cevir("{0}% filled · {1} of {2} rows · {3} of {4} measurements", y.yuzde, y.satirDolu, y.satirToplam, y.olcuDolu, FORM_OLCU_SAYISI)}</span>
    </div>
  );
}

/** Hata cümleleri: düğme işleminin ve kaydı tutmayan alanların (her cümle bir kez). */
function Hatalar({ mesajlar }: { mesajlar: readonly string[] }) {
  return <>{mesajlar.map((m) => <Hata key={m} mesaj={m} />)}</>;
}

export function MontajForm({ v, setVeri }: { v: MontajRiserYaniti; setVeri: Dispatch<SetStateAction<MontajRiserYaniti | null>> }) {
  const { ben } = useOturum();
  const { pdfSec, pencere } = usePdfSec();
  /** Düğme işleminin (print-out, imzalı çıktı, kaldırma) hata cümlesi. */
  const [islem, setIslem] = useState<string | null>(null);
  /** Kaydı tutmayan alanların cümleleri, alan anahtarıyla; alan yeniden kaydedilince düşer. */
  const [hatalar, setHatalar] = useState<Readonly<Record<string, string>>>({});
  const [mesgul, setMesgul] = useState<Mesgul | null>(null);
  /** "Fitted By" varsayılanı: görünür, kişi değiştirebilir; sunucuya ayrıca yazılmaz, satır doldurulurken satıra yazılır. */
  const [fittedBy, setFittedBy] = useState(ben?.ad ?? "");
  const [siraya] = useState(isSirasi);
  // En son bilinen sunucu durumu: sıradaki gövde bundan kurulur (yanıt geldiği anda yazılır; çizimi beklemez).
  const son = useRef(v);
  useLayoutEffect(() => {
    son.current = v;
  }, [v]);
  const al = useCallback((y: MontajRiserYaniti) => {
    son.current = y;
    setVeri(y);
  }, [setVeri]);
  const tazele = useMemo(() => siraliYenile(() => api.get<MontajRiserYaniti>(MONTAJ_UCU.riser(v.rn)), al), [v.rn, al]);

  const k = v.kayit;
  if (!k) return null;

  // Formu doldurma yetkisi olmayan kişi (ör. yalnız imzalı çıktıyı yükleyen) alanları kapalı görür: kaydedilmeyecek bir değer
  // ekranda (ve tarayıcıdan alınan çıktıda) yazılı kalmasın.
  const salt = k.kapali || !(ben && yetkiliMi(ben.rol, "assembly"));
  const oneriler = v.oneriler ?? [];
  const eksikler = k.formEksikleri ?? [];
  const adim = formAdimi({ kayit: k, akis: v.akis, raporUyumu: v.raporUyumu });
  const ndt = ndtBandi({ kayit: k, akis: v.akis, raporUyumu: v.raporUyumu });
  const ndtEngel = Boolean(v.akis.ndtEngel);
  const etiketler = formEtiketleri({ akis: v.akis, raporUyumu: v.raporUyumu, kayit: k });
  const mesajlar = [...new Set([islem, ...Object.values(hatalar)].filter((m): m is string => Boolean(m)))];
  const kilitli = Boolean(mesgul);

  /**
   * Bir alanı kaydeder. Gövde sıra geldiğinde, en son sunucu durumundan kurulur (`kur` null dönerse değer zaten kayıttadır,
   * istek gitmez). Yanıt sayfanın tam durumudur. false: kayıt tutmadı; cümlesi alan yeniden kaydedilene kadar görünür.
   */
  const kaydet = (anahtar: string, kur: (guncel: MontajKaydiYaniti) => MontajFormGovdesi | null): Promise<boolean> =>
    siraya(async () => {
      const guncel = son.current.kayit;
      if (!guncel || guncel.tur !== "form") return false;
      try {
        const govde = kur(guncel);
        if (govde) al(await montajFormGuncelle(guncel.id, govde));
        setHatalar((h) => {
          if (!(anahtar in h)) return h;
          const kalan = { ...h };
          delete kalan[anahtar];
          return kalan;
        });
        return true;
      } catch (e) {
        const mesaj = hataMetni(e);
        setHatalar((h) => ({ ...h, [anahtar]: mesaj }));
        // Kayıt başka yerden değişmiş olabilir (ör. imzalı çıktı yüklendi ve kayıt kapandı): ekran sunucudaki durumu göstersin.
        await tazele().catch(() => undefined);
        return false;
      }
    });

  /** Bir düğme işlemini bekleyen alan kayıtlarının ardından yürütür, sonra sayfayı sunucudan tazeler (işlem düşse de). */
  const calis = (ad: Mesgul, is: () => Promise<string | null | void>): Promise<void> => {
    setIslem(null);
    setMesgul(ad);
    return siraya(async () => {
      let mesaj: string | null = null;
      try {
        const sonuc = await is();
        mesaj = typeof sonuc === "string" ? sonuc : null;
      } catch (e) {
        mesaj = hataMetni(e);
      }
      try {
        await tazele();
      } catch (e) {
        mesaj ??= hataMetni(e);
      }
      setIslem(mesaj);
      setMesgul(null);
    });
  };

  const d: Duzenleme = {
    kapali: k.kapali,
    salt,
    oneriler,
    fittedBy,
    satirYaz: (sira, alanAdi, kur) =>
      kaydet(`satir:${sira}:${alanAdi}`, (g) => {
        const satir = g.satirlar.find((s) => s.sira === sira);
        const yama = satir ? kur(satir) : null;
        return yama ? satirGovdesi(sira, yama) : null;
      }),
  };

  const olcuYaz = async (tip: MontajOlcumTipi, hat: MontajOlcumHatti, metin: string): Promise<"yazildi" | "bicim" | "ret"> => {
    if (olcuYamasi(tip, hat, metin, null).tur === "hata") return "bicim";
    const ok = await kaydet(`olcu:${tip}:${hat}`, (g) => {
      const y = olcuYamasi(tip, hat, metin, g.olcumler.find((o) => o.tip === tip && o.hat === hat));
      return y.tur === "yaz" ? { olcumler: [y.olcum] } : null;
    });
    return ok ? "yazildi" : "ret";
  };

  // "Generate print-out": yalnız işaret yazar; kapının kuralı sunucunundur (düğme `formEksikleri` boşken açılır).
  const yazdir = () => calis("yazdir", async () => {
    await montajFormYazdir(k.id);
  });
  // İmzalı çıktı Finishing'in Assembly raporudur: var olan uç ve bütün kapıları (NDT ön koşulu dahil), ardından kaydın kapanması.
  // İkinci adım düşerse rapor yüklü kalır; tazelenen sayfa "Close the record" sunar.
  const imzaliYukle = async () => {
    const r = await pdfSec(cevir("Upload signed print-out"), ".pdf,image/*");
    if (!r) return;
    const soru = dosyaRiserSorusu(r.dosya.name, v.rn);
    if (soru && !window.confirm(soru)) return;
    await calis("imzali", async () => {
      const istek = akisRaporIstegi(v.rn, r.dosya, sayfaAlanlari(r.sayfa));
      await dosyaYukle(istek.yol, istek.dosya, istek.alanlar, istek.ekDosyalar, istek.dosyaAlan);
      await montajFormImzala(k.id);
    });
  };
  const kapat = () => calis("imzali", async () => {
    await montajFormImzala(k.id);
  });
  // Kapalı kaydın imzalı çıktısı Finishing'den kaldırılır; montaj kaydına dokunulmaz (kapalı kalır, verisini korur).
  const imzaliKaldir = () => {
    if (!window.confirm(cevir("Remove the signed print-out of {0} from Finishing? The assembly record stays closed and keeps its data; the Finishing step waits for the signed print-out again. The removal is kept in the audit log.", v.rn))) return;
    void calis("kaldir", async () => {
      await asmRaporKaldir(v.rn);
    });
  };
  // Açık formu siler. Form türünde ikinci çağrı (Finishing bağı) yapılmaz: yüklenmiş bir belge istenmeden kaldırılmasın.
  const kaldir = () => {
    const soru = kaldirmaSorusu(v);
    if (!soru || !window.confirm(soru)) return;
    void calis("kaldir", async () => {
      const s = await kaldirmayiYurut("form", { kayitKaldir: () => montajKaldir(k.id), bagKaldir: () => asmRaporKaldir(v.rn) });
      return s.adim === "hata" ? s.mesaj : null;
    });
  };

  // Print-out işaretinden sonra eksiltilen form kapanmaz (sunucunun kuralı): açık kayıtta iki düğme de kapanır, eksikler altlarında yazar.
  const acikEksikler = k.kapali ? [] : eksikler;
  const eksikSatiri = acikEksikler.length > 0 && <p className="mt-1 text-xs text-ink-3">{yazdirmaIpucu(acikEksikler)}</p>;
  const imzaliDugmesi = (
    <div className="mt-2">
      <Buton islem="evrak" tur="birincil" mesgul={mesgul === "imzali"} disabled={ndtEngel || acikEksikler.length > 0 || kilitli} title={ndtEngel ? cevir("Blocked until every line has an NDT report or No record available") : yazdirmaIpucu(acikEksikler)} onClick={imzaliYukle}>{cevir("Upload signed print-out")}</Buton>
      {eksikSatiri}
    </div>
  );
  const kapatDugmesi = (
    <div className="mt-2">
      <Buton islem="evrak" tur="birincil" mesgul={mesgul === "imzali"} disabled={acikEksikler.length > 0 || kilitli} title={yazdirmaIpucu(acikEksikler)} onClick={kapat}>{cevir("Close the record")}</Buton>
      {eksikSatiri}
    </div>
  );

  return (
    <Sayfa
      ust={<Link to="/assembly" className="normal-case tracking-normal text-orange">{cevir("← Finishing")}</Link>}
      baslik={
        <>
          Assembly Traceability Record {v.rn} <Etiket className="ml-2 align-middle">{cevir("OSIUK-W-FM-0012 · digital form")}</Etiket>
          {etiketler.map((e, n) => <Fragment key={n}> <Etiket tone={e.ton} className="align-middle">{e.metin}</Etiket></Fragment>)}
        </>
      }
      alt={cevir("Fields from the FM-0012 template (edition C). Data is entered during assembly, then the print-out, the wet signatures and the signed document uploaded back.")}
    >
      <Hatalar mesajlar={mesajlar} />
      <div className="space-y-2">
        {ndt && <Band tip="not">{ndt}</Band>}
        <Doluluk k={k} />
      </div>

      <Kutu
        baslik={cevir("Header")}
        aksiyon={!k.kapali && k.kaldirilabilir ? <Buton kucuk islem="assembly" tur="tehlike" mesgul={mesgul === "kaldir"} disabled={kilitli} onClick={kaldir}>{cevir("Remove")}</Buton> : undefined}
      >
        <div className="grid gap-x-4 gap-y-3 sm:grid-cols-3">
          <Kv ad={cevir("Issued By (automatic)")}>{k.acan}</Kv>
          <Kv ad={cevir("Issue Date (automatic)")}>{tarih(k.ts)}</Kv>
          {FM12_BASLIK.map(([ad, deger]) => <Kv key={ad} ad={ad}>{deger}</Kv>)}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <b className="text-sm">Assembly Serial No.</b>
          <Girdi
            className="max-w-[200px]"
            placeholder={cevir("Scan/enter SN")}
            maxLength={FORM_ALAN_SINIRI.assemblySn}
            deger={k.assemblySnYazim ?? ""}
            kapali={salt}
            yaz={(m) => kaydet("assemblySn", (g) => ((g.assemblySnYazim ?? "") === m ? null : { assemblySnYazim: m || null }))}
            // Riser kaydındaki ve OEM kitabındaki numara öneridir; alan boş başlar, kişi plakadan okuyup yazar ya da öneriyi alır.
            // Çip alanda DURAN metinle karşılaştırır: kaydı tutmayan değer alanda dururken kayıttaki eski değere bakılmaz.
            yaninda={(taslak) => !k.kapali && (
              <OneriCipleri
                gruplar={oneriGruplari(oneriler, null, null)}
                mevcut={() => baslikOneriMevcut(taslak, k)}
                kullan={(g) => void kaydet("assemblySn", (gk) => (gk.assemblySnYazim?.trim() ? null : { assemblySnYazim: g.deger }))}
              />
            )}
          />
        </div>
      </Kutu>

      <Kutu baslik={cevir("Important details")} aksiyon={<span className="text-ink-3">Important Details Noted During Assembly</span>}>
        <MetinAlani satir={3} maxLength={FORM_ALAN_SINIRI.onemliNot} deger={k.onemliNot ?? ""} kapali={salt} yaz={(m) => kaydet("onemliNot", (g) => ((g.onemliNot ?? "") === m ? null : { onemliNot: m || null }))} />
      </Kutu>

      {k.hal === "form_kapali" && <MontajSureklilik kayit={k} />}
      <Kutu baslik={cevir("Traceability details: 30 parts")} padded={false}>
        <div className="px-3 pt-3">
          <p className="text-sm text-ink-2">
            {cevir("Select the status of each row. For a fitted row, enter the identity of every unit as stamped on the part; lists (bolts, washers, locking inserts, buoyancy modules) are entered unit by unit under their row.")}{" "}
            {cevir("The lists can be entered whatever the row status is.")}
          </p>
          {/* Varsayılan yalnız form doldurulurken çizilir: kapalı ya da doldurulamayan formda bakan kişinin adı kayıt verisi gibi görünmesin. */}
          {!salt && (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
              <b>Fitted By</b>
              <span className="text-ink-3">{cevir("for the rows you fill")}</span>
              <input className={cn(girdi, "max-w-[200px]")} value={fittedBy} maxLength={FORM_ALAN_SINIRI.fittedBy} autoComplete="off" onChange={(e) => setFittedBy(e.target.value)} />
            </div>
          )}
        </div>
        {k.satirlar.map((x) => <SatirDuzenle key={x.sira} x={x} d={d} />)}
      </Kutu>

      <Kutu baslik={cevir("Stick outs and box gaps")}>
        {olcuGruplari(k.olcumler, "in").map((g, n) => (
          <Fragment key={g.tip}>
            <div className={n ? "mt-3 text-xs font-medium text-ink-3" : "text-xs font-medium text-ink-3"}>{g.baslik}</div>
            <div className="mt-1 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {g.hucreler.map(({ hat, ad, hucre }) => <OlcuGirdisi key={hat} ad={ad} hucre={hucre} kapali={salt} yaz={(m) => olcuYaz(g.tip, hat, m)} />)}
            </div>
          </Fragment>
        ))}
        {k.olcumler.some((o) => o.sokumOncesi != null) && <p className="mt-3 text-xs text-ink-3">{cevir("Measurements taken before disassembly are shown under each field for comparison only; no tolerance is checked.")}</p>}
      </Kutu>

      <Kutu baslik={cevir("Technician's comments")}>
        <MetinAlani satir={2} maxLength={FORM_ALAN_SINIRI.yorum} deger={k.yorum ?? ""} kapali={salt} yaz={(m) => kaydet("yorum", (g) => ((g.yorum ?? "") === m ? null : { yorum: m || null }))} />
      </Kutu>

      {/* Uzun formda, telefonda görünür kalsın diye cümleler son kutunun üstünde de durur. */}
      <Hatalar mesajlar={mesajlar} />
      <Kutu baslik={cevir("Review and approval")}>
        <div className="scroll-thin overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="border-b border-line">
                <th className={tabloBas}>{cevir("Signature")}</th>
                <th className={tabloBas}>{cevir("Name")}</th>
                <th className={tabloBas}>{cevir("Date")}</th>
                <th className={tabloBas}>{cevir("System equivalent")}</th>
              </tr>
            </thead>
            <tbody>
              {ONAY_SATIRLARI.map(([satir, baslik], n) => (
                <tr key={satir} className="border-b border-line last:border-0">
                  <td className={tabloHucre}>{baslik}</td>
                  <td className={tabloHucre}>
                    <Girdi maxLength={FORM_ALAN_SINIRI.onayAdi} deger={k.onay?.[satir]?.ad ?? ""} kapali={salt} yaz={(m) => kaydet(`onay:${satir}:ad`, (g) => ((g.onay?.[satir]?.ad ?? "") === m ? null : onayGovdesi(satir, { ad: m || null })))} />
                  </td>
                  <td className={tabloHucre}>
                    <Girdi type="date" deger={k.onay?.[satir]?.tarih ?? ""} kapali={salt} yaz={(m) => kaydet(`onay:${satir}:tarih`, (g) => ((g.onay?.[satir]?.tarih ?? "") === m ? null : onayGovdesi(satir, { tarih: m || null })))} />
                  </td>
                  {n === 0 && <td rowSpan={ONAY_SATIRLARI.length} className={tabloHucre}><Etiket className="whitespace-normal">{cevir("signatures are wet signed on print-out; system only asks for signed document (K3)")}</Etiket></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {adim === "yazdir" && (
          <>
            <div className="mt-3"><Doluluk k={k} /></div>
            <div className="mt-2">
              <Buton islem="assembly" tur="birincil" mesgul={mesgul === "yazdir"} disabled={eksikler.length > 0 || kilitli} title={yazdirmaIpucu(eksikler)} onClick={yazdir}>{cevir("Generate print-out")}</Buton>
            </div>
            {/* Düğme neden kapalı: eksikler ipucunda da yazar, ama dokunmatik ekranda ipucu açılmaz (form sahada telefonda doldurulur). */}
            {eksikler.length > 0 && <p className="mt-1 text-xs text-ink-3">{yazdirmaIpucu(eksikler)}</p>}
          </>
        )}
        {adim === "imzali_yukle" && (
          <>
            <Band tip="not" className="mt-3">{cevir("Print-out generated. Wet signatures are done on paper; the signed document is uploaded back here. If you change a field now, print the form again before signing.")}</Band>
            {imzaliDugmesi}
          </>
        )}
        {adim === "kayit_kapat" && (
          <>
            <Band tip="not" className="mt-3">{cevir("The signed print-out is uploaded, but the record is not closed yet.")}</Band>
            {kapatDugmesi}
          </>
        )}
        {adim === "kapali" && (
          <>
            <Band tip="ok" className="mt-3">
              {cevir("Signed print-out uploaded. The assembly record is closed and can no longer be changed.")}
              {k.rapor?.duruyor && <> <a className="underline" href={belgeLinki(k.rapor.dokumanId, k.rapor.sayfa)} target="_blank" rel="noreferrer">{cevir("open document")}</a></>}
              {k.rapor?.duruyor && k.rapor.sayfa && <> <Cip>{sayfaEtiketi(k.rapor.sayfa)}</Cip></>}
            </Band>
            {/* Yalnız kaydın belgesi Finishing'in raporuyken: bugünkü rapor kaldırma düğmesinin kapalı formdaki karşılığı. */}
            {v.raporUyumu === "ayni" && (
              <div className="mt-2">
                <Buton kucuk islem="evrak" mesgul={mesgul === "kaldir"} disabled={kilitli} onClick={imzaliKaldir}>{cevir("Remove signed print-out")}</Buton>
              </div>
            )}
          </>
        )}
        {adim === "imzali_yeniden" && (
          <>
            <Band tip="not" className="mt-3">{cevir("The signed print-out of this closed record was removed from Finishing. The record keeps its data; upload the signed print-out again to complete the Finishing step.")}</Band>
            {imzaliDugmesi}
          </>
        )}
        {adim === "baska_dosya" && (
          <>
            <Band tip="not" className="mt-3">{cevir("Finishing holds another Assembly report file than this record.")}</Band>
            {kapatDugmesi}
          </>
        )}
      </Kutu>
      {pencere}
    </Sayfa>
  );
}
